import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Reveal from '../components/Reveal';
import HeroDemo from '../components/HeroDemo';
import MiniDemoGastos from '../components/MiniDemoGastos';
import SeccionIA, { ProBadge } from '../components/LandingIA';
import { contar, prefiereQuieto, useEnVista } from '../utils/motion';
import { usePageMeta } from '../utils/pageMeta';
import { usePlanesPublicos } from '../utils/planesPublicos';
import { pesos } from '../utils/muestra';
import Logo from '../components/Logo';
import LandingReferidos from '../components/LandingReferidos';

const FOCUS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:focus-visible:ring-blue-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900';
const BTN_PRIMARIO = `inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-7 py-3.5 rounded-xl text-base lp-press shadow-lg shadow-blue-500/25 ${FOCUS}`;
const BTN_SECUNDARIO = `inline-flex items-center justify-center gap-2 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-800 dark:text-gray-100 font-semibold px-7 py-3.5 rounded-xl text-base border border-gray-300 dark:border-slate-600 lp-press ${FOCUS}`;

// Cada tarjeta está trazada al código (rutas en App.jsx, candados "plan_required" en api/controllers).
// pro: true = solo Plan Pro. `corto` se ve siempre; `detalle` se despliega al tocar la tarjeta.
const GRUPOS = [
  {
    id: 'gastos', titulo: 'Controla tus gastos', sub: 'Sabe en qué se va tu dinero y deja de pasarte del presupuesto.',
    items: [
      { icon: '🏷️', title: 'Categorías y reglas automáticas', corto: 'Cada gasto cae en su categoría.', detalle: 'Crea reglas (por ejemplo, si la referencia contiene “uber”, va a Transporte) y tus movimientos se categorizan solos.' },
      { icon: '📅', title: 'Disponible hoy', corto: 'Un número: cuánto puedes gastar hoy.', detalle: 'Se calcula después de tus gastos comprometidos y tus metas de ahorro, para que no te quedes sin dinero antes de tu siguiente cobro.' },
      { icon: '✉️', title: 'Presupuesto por quincena y sobres', corto: 'Reparte tu dinero antes de gastarlo.', detalle: 'Arma tu presupuesto por mes o por quincena y asigna cada monto a un sobre. En tu panel verás un aviso cuando un presupuesto llegue al 80 % y al 100 %.' },
      { icon: '🐜', title: 'Gastos hormiga', pro: true, corto: 'Los gastitos que se repiten y se acumulan.', detalle: 'Detecta gastos pequeños y frecuentes del mes (cafeterías, tiendas de conveniencia, apps de transporte) y calcula cuánto sumarían en un año.' },
      { icon: '📊', title: 'Comparativa con el mes anterior', corto: '¿Gastas más o menos que antes?', detalle: 'Tu panel compara tus gastos e ingresos del mes contra el mes anterior. Necesita movimientos de al menos dos meses.' },
      { icon: '💡', title: 'Recomendaciones y pronóstico', pro: true, corto: 'Avisos sobre tus patrones.', detalle: 'Gastos fuera de lo normal, pronóstico de fin de mes y score financiero, a partir de tu historial.' },
      { icon: '🔄', title: 'Ingresos recurrentes y suscripciones', pro: true, corto: 'Que ningún cargo fijo te sorprenda.', detalle: 'Registra tus ingresos fijos y tus suscripciones para ver lo que viene en el mes.' },
      { icon: '🚨', title: 'Alertas de cargos raros', corto: 'Te avisamos si algo no cuadra.', detalle: 'Un posible cobro duplicado, una suscripción que subió de precio o un monto mucho más alto de lo normal aparecen en «Revisa esto» de tu panel. En Pro, la IA te explica por qué se ve raro.' },
      { icon: '🗓️', title: 'Tu semana en un vistazo', corto: 'Cada lunes, cómo te fue.', detalle: 'Un resumen semanal con lo que gastaste, en qué se fue más y cómo vas contra tus presupuestos, para corregir el rumbo antes de que acabe la quincena.' },
    ],
  },
  {
    id: 'dinero', titulo: 'Todo tu dinero en un solo lugar', sub: 'Cuentas, tarjetas, inversiones, deudas y metas.',
    items: [
      { icon: '🏦', title: 'Cuentas en pesos y dólares', corto: 'Una cuenta puede ser en USD.', detalle: 'Registra ingresos y gastos en USD y la app los convierte a pesos con el tipo de cambio FIX de Banxico de la fecha del movimiento.' },
      { icon: '💳', title: 'Tarjetas y meses sin intereses', corto: 'Cuánto te falta por pagar de cada compra.', detalle: 'Registra tus compras a MSI en tu tarjeta de crédito y ve cuánto te queda por pagar de cada una, mes a mes.' },
      { icon: '🔁', title: 'Transferencias entre cuentas', corto: 'De una cuenta a otra, con su registro.', detalle: 'Registra lo que mueves de una cuenta a otra, con su origen y su destino.' },
      { icon: '📈', title: 'Inversiones', corto: 'CETES, fondos, acciones y cripto.', detalle: 'Registra CETES, pagarés, fondos, acciones o ETF y cripto, y mira su valuación. MONIMEX no se conecta a tu casa de bolsa: tú capturas los datos.' },
      { icon: '💎', title: 'Patrimonio neto', pro: true, corto: 'Cuánto vales hoy.', detalle: 'Suma tus cuentas y tus inversiones (en pesos) y resta tus deudas.' },
      { icon: '📉', title: 'Deudas', pro: true, corto: 'Cuánto debes y cuánto falta.', detalle: 'Lleva tus deudas activas con su saldo, y se restan en tu patrimonio neto.' },
      { icon: '🎯', title: 'Metas de ahorro', corto: 'Cuánto juntar y para cuándo.', detalle: 'Define tu monto objetivo y tu fecha límite, y usa la calculadora para saber cuánto apartar.' },
      { icon: '👥', title: 'Espacios compartidos', pro: true, corto: 'Invita a tu contador o a tu hogar.', detalle: 'Invita a alguien que ya tenga cuenta en MONIMEX: como contador, con acceso de solo lectura a tus movimientos y CFDI, o a tu hogar, con presupuestos compartidos. Puedes revocar el acceso cuando quieras.' },
    ],
  },
];

const GRUPO_FREELANCE = {
  id: 'facturas', titulo: '¿Facturas o cobras en dólares? También.',
  sub: 'MONIMEX entiende tus CFDI y tus cobros en USD, y te ayuda a calcular cuánto apartar de impuestos.',
  items: [
    { icon: '🧾', title: 'Importa tus CFDI 3.3 y 4.0', corto: 'Tus facturas, convertidas en movimientos.', detalle: 'Sube los XML: lo emitido entra como ingreso y lo recibido como gasto, con emisor, monto y fecha, sin duplicados. En el plan gratis, hasta 10 al mes.' },
    { icon: '💵', title: 'CFDI y cobros en USD', corto: 'Con el tipo de cambio correcto.', detalle: 'Si el CFDI viene en USD, se convierte a pesos con el tipo de cambio del propio CFDI; si no lo trae, con el FIX de Banxico de esa fecha.' },
    { icon: '🧮', title: 'Estimador de ISR e IVA', pro: true, corto: 'Cuánto apartar cada mes.', detalle: 'Para RESICO y honorarios, con lo que cobras y gastas. Es una estimación orientativa, no tu declaración. También hay una calculadora RESICO pública, gratis y sin registro.', href: '/calculadora-resico', link: 'Probar la calculadora RESICO' },
    { icon: '✅', title: 'Sugerencias de deducibilidad', pro: true, corto: '¿Este CFDI podría deducirse?', detalle: 'Reglas primero y, solo en los casos dudosos, IA. Siempre: confírmalo con tu contador.' },
    { icon: '💼', title: 'Proyectos con comisiones', pro: true, corto: 'Cada proyecto con sus cobros.', detalle: 'Lleva cada proyecto con sus participantes y la comisión de cada uno, en porcentaje o monto fijo.' },
  ],
};

const PASOS = [
  { n: '1', title: 'Crea tu cuenta', desc: 'Con tu correo, sin tarjeta. Al registrarte empiezas con 14 días de Pro.' },
  { n: '2', title: 'Registra o importa tus movimientos', desc: 'Captura ingresos y gastos a mano, importa un CSV de tu banco o un estado de cuenta en PDF, o sube tus CFDI.' },
  { n: '3', title: 'Ve en qué se va tu dinero', desc: 'Tu panel junta tus categorías, tus presupuestos y tu disponible del día para que sepas dónde ajustar.' },
];

const FAQS = [
  { q: "¿Necesito conectar mi banco?", a: "No. MONIMEX no se conecta a tu banco y nunca te pedimos la contraseña de tu banca en línea. Tú capturas tus movimientos, importas un CSV o un estado de cuenta en PDF, o subes tus CFDI." },
  { q: "¿Hay app para el celular?", a: "Todavía no hay app nativa en App Store ni Play Store. MONIMEX es una PWA: la abres en el navegador y la instalas en la pantalla de inicio de tu teléfono o computadora, y funciona como una app." },
  { q: "¿Cómo me ayuda a gastar menos?", a: "Categoriza tus movimientos (con reglas que tú defines), te muestra cuánto gastas por categoría y cuánto te queda disponible hoy, y te avisa en tu panel cuando un presupuesto llega al 80 % y al 100 %. Con Pro, además detecta tus gastos hormiga: gastos pequeños que se repiten en el mes, y calcula cuánto sumarían en un año." },
  { q: "¿Puedo llevar presupuesto por quincena, por sobres y mis MSI?", a: "Sí. Puedes armar tu presupuesto por mes completo o por quincena y activar el modo sobres. Las compras a meses sin intereses las registras en tu tarjeta con su número de meses y ves cuánto te queda por pagar de cada una." },
  { q: "¿Cuánto cuesta y qué incluye la prueba?", a: "Al registrarte tienes 14 días de Pro sin tarjeta. Después puedes quedarte en el plan gratuito (hasta 150 transacciones, 10 CFDI al mes y 3 cuentas) o pasar a Pro, con precio fundador de $59 MXN al mes. Los precios están en pesos mexicanos." },
  { q: "¿Qué incluye la IA y qué es de Pro?", a: "En el plan gratuito puedes hacerle 3 preguntas al mes al asistente para conocerlo; todo lo demás de IA es de Pro. Con Pro tienes un asistente para preguntar sobre tu propio dinero (solo temas de la app, con tope de 100 preguntas al mes), informe mensual narrado, alertas explicadas, escaneo de tickets, lectura de estados de cuenta en PDF, categorización que aprende de ti, sugerencias de deducibilidad, presupuesto sugerido y coach de metas." },
  { q: "¿Qué pasa con mis datos cuando uso la IA?", a: "Los procesan proveedores de IA en EE. UU. y no se usan para entrenar modelos. Antes de enviar algo se quitan tu RFC, CURP, correos y números de cuenta o tarjeta, y las fotos se envían sin metadatos. Puedes leer más en la página de Seguridad." },
  { q: "¿La IA inventa cifras?", a: "No: los montos salen siempre de tus datos y de cálculos de la app, y el asistente verifica las cifras antes de mostrarlas. Si no puede verificarlas, prefiere no responder. Aun así, la IA puede equivocarse al interpretar, por eso lo fiscal siempre va con el aviso de confirmarlo con tu contador." },
  { q: "¿Puedo escanear tickets o importar estados de cuenta en PDF?", a: "Escanear tickets es de Pro: lee el texto de la foto y la IA solo ayuda si falta un dato. Los estados de cuenta en PDF se leen con un lector por banco y el resultado se concilia con el saldo; en Pro, si no cuadra, la IA lo reintenta. Si aun así no cuadra, te avisamos para que lo revises antes de importar." },
  { q: "¿Puedo registrar mis inversiones?", a: "Sí. Puedes registrar CETES, pagarés, fondos, acciones o ETF y cripto, ver su valuación y, con Pro, sumarlas a tu patrimonio neto. MONIMEX no se conecta a tu casa de bolsa: tú capturas los datos." },
  { q: "¿Qué pasa con mis datos si me voy?", a: "Son tuyos. Puedes descargar toda tu información en JSON y eliminar tu cuenta cuando quieras, conforme a la LFPDPPP (derechos ARCO). No vendemos tus datos." },
  { q: "¿Puedo compartir mi información con mi contador?", a: "Con Pro puedes invitar a alguien que ya tenga cuenta en MONIMEX como contador, con acceso de solo lectura a tus movimientos y CFDI, o a tu hogar, para compartir presupuestos. Tú envías la invitación y puedes revocar el acceso cuando quieras." },
  { q: "¿Qué es un CFDI y MONIMEX emite mis facturas?", a: "El CFDI es la factura electrónica del SAT. MONIMEX no timbra facturas: importa los XML (versiones 3.3 y 4.0) que ya emitiste o recibiste y crea el movimiento con emisor, monto y fecha, sin duplicarlo. Lo emitido cuenta como ingreso y lo recibido como gasto. El plan gratis importa hasta 10 al mes." },
  { q: "¿Sirve si cobro en dólares?", a: "Sí. Puedes registrar ingresos en USD y la app los convierte a pesos con el tipo de cambio FIX publicado por Banxico para la fecha del movimiento. Si importas un CFDI en USD, usa el tipo de cambio que trae el propio CFDI y, si no lo trae, el FIX de esa fecha." },
  { q: "¿Calcula cuánto ISR pago en RESICO u honorarios?", a: "Con Pro, estima tu ISR y tu IVA provisionales (con la tabla RESICO o con la de honorarios) y te sugiere cuánto apartar cada mes. También hay una calculadora RESICO pública, gratis y sin registro. Es una estimación orientativa, no tu declaración: para presentarla consulta a tu contador." },
  { q: "¿Me dice qué CFDI son deducibles?", a: "Con Pro te sugiere cuáles de tus CFDI recibidos podrían ser deducibles: primero aplica reglas y solo usa IA en los casos ambiguos. Es una sugerencia, no asesoría fiscal: confírmalo con tu contador." },
];

const FAQ_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
};

const mxn = (n) => pesos(n);

function FaqItem({ q, a, id, open, onToggle }) {
  return (
    <div className="border-b border-gray-100 dark:border-slate-700 last:border-0">
      <h3>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={`faq-${id}`}
          onClick={onToggle}
          className={`w-full flex items-center justify-between py-4 text-left gap-4 ${FOCUS} rounded`}>
          <span className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">{q}</span>
          <span aria-hidden="true" className={`shrink-0 text-gray-500 dark:text-gray-400 transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] ${open ? 'rotate-45' : ''}`}>+</span>
        </button>
      </h3>
      <div className="lp-acc" data-open={open}>
        <div>
          <p id={`faq-${id}`} className="pb-4 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{a}</p>
        </div>
      </div>
    </div>
  );
}

// Tarjeta de función: toca (o Enter) para ver el detalle; el icono reacciona y el panel
// se despliega con el mismo acordeón del FAQ. `tenue` atenúa las que no coinciden con el filtro.
function FeatureCard({ f, tenue, H = 'h4' }) {
  const [open, setOpen] = useState(false);
  const id = `fc-${f.title.replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <div className={`fc-card h-full bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 lp-card ${tenue ? 'fc-tenue' : ''}`}>
      <H>
        <button type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpen(o => !o)}
          className={`fc-btn w-full text-left p-6 pb-2 rounded-t-2xl flex items-start gap-3 ${FOCUS}`}>
          <span aria-hidden="true" className="fc-icon text-3xl leading-none">{f.icon}</span>
          <span className="flex-1 font-bold text-lg leading-snug">{f.title}{f.pro && <ProBadge />}</span>
          <span aria-hidden="true" className={`shrink-0 text-gray-500 dark:text-gray-400 transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] ${open ? 'rotate-45' : ''}`}>+</span>
        </button>
      </H>
      <p className="px-6 text-gray-700 dark:text-gray-200 text-sm leading-relaxed">{f.corto}</p>
      <div className="lp-acc" data-open={open}>
        <div>
          <div id={id} className="px-6 pt-2 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
            {f.detalle}
            {f.href && <div><Link to={f.href} className={`inline-block mt-2 font-semibold text-blue-700 dark:text-blue-300 underline rounded ${FOCUS}`}>{f.link}</Link></div>}
          </div>
        </div>
      </div>
      <div className="h-5" />
    </div>
  );
}

// Interruptor Todo / Gratis / Pro: atenúa (opacidad + escala, sin layout) las tarjetas que no coinciden.
function FiltroPlan({ valor, onChange }) {
  const op = [['todo', 'Todo'], ['gratis', 'Incluido en Gratis'], ['pro', 'Solo en Pro']];
  return (
    <div role="group" aria-label="Resaltar funciones por plan" className="inline-flex bg-gray-200 dark:bg-slate-700 rounded-xl p-1 gap-1">
      {op.map(([k, t]) => (
        <button key={k} type="button" aria-pressed={valor === k} onClick={() => onChange(k)}
          className={`min-h-[44px] px-3 sm:px-4 py-2 rounded-lg text-sm font-semibold lp-press ${FOCUS} ${valor === k ? 'bg-white dark:bg-slate-600 text-gray-900 dark:text-white shadow' : 'text-gray-700 dark:text-gray-200'}`}>{t}</button>
      ))}
    </div>
  );
}

const tenueSegun = (filtro, pro) => (filtro === 'gratis' && pro) || (filtro === 'pro' && !pro);

function GrupoFunciones({ g, filtro, h = 'h3' }) {
  const H = h;
  return (
    <div>
      <Reveal><div className="mb-5">
        <H className="text-2xl font-black">{g.titulo}</H>
        <p className="text-gray-600 dark:text-gray-300">{g.sub}</p>
      </div></Reveal>
      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {g.items.map((f, i) => (
          <li key={f.title}>
            <Reveal delay={(i % 3) * 90} className="h-full">
              <FeatureCard f={f} tenue={tenueSegun(filtro, !!f.pro)} />
            </Reveal>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Cifra de la franja: cuenta hasta su valor al entrar en pantalla (nada de animar si prefiere quieto).
function Dato({ valor, sufijo = '', label }) {
  const raiz = useRef(null);
  const num = useRef(null);
  const visto = useEnVista(raiz, 0.5);
  useEffect(() => {
    if (!visto || prefiereQuieto() || !num.current) return;
    return contar(num.current, 0, valor, { duracion: 900, formato: (n) => `${n}${sufijo}` });
  }, [visto, valor, sufijo]);
  return (
    <li ref={raiz}>
      <div className="text-2xl sm:text-3xl font-black mb-1 tabular-nums"><span ref={num}>{`${valor}${sufijo}`}</span></div>
      <div className="text-blue-50 text-xs sm:text-sm">{label}</div>
    </li>
  );
}

// Ejemplo ilustrativo del cobro en USD (sección de facturas): el número cuenta al entrar en pantalla.
function EjemploUSD() {
  const raiz = useRef(null);
  const num = useRef(null);
  const visto = useEnVista(raiz, 0.5);
  useLayoutEffect(() => {
    if (!prefiereQuieto() && num.current) num.current.textContent = '$0';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!visto || prefiereQuieto() || !num.current) return;
    return contar(num.current, 0, 48720, { duracion: 900, formato: pesos });
  }, [visto]);
  return (
    <figure ref={raiz} className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 shadow-lg overflow-hidden" aria-label="Ejemplo ilustrativo: un CFDI de 2,800 USD convertido a pesos">
      <figcaption className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-semibold">Ejemplo ilustrativo · datos de muestra</figcaption>
      <div className="p-5 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium">Cliente EE. UU. — 2,800 USD</p>
          <p className="text-xs text-gray-600 dark:text-gray-300">CFDI importado · tipo de cambio de la fecha: 1 USD = $17.40</p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-lg font-bold text-green-700 dark:text-green-400 tabular-nums">+<span ref={num} className="inline-block min-w-[8ch] text-right">{pesos(48720)}</span></p>
          <span className="inline-block text-[10px] leading-none font-semibold px-1.5 py-1 rounded-md bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200">Ingreso</span>
        </div>
      </div>
    </figure>
  );
}

// Precio que "rueda" al cambiar de periodo (solo al alternar, no al cargar).
function PrecioAnimado({ valor, periodo }) {
  const ref = useRef(null);
  const previo = useRef(periodo);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const cambioPeriodo = previo.current !== periodo;
    previo.current = periodo;
    if (!cambioPeriodo || prefiereQuieto()) { el.textContent = mxn(valor); return; }
    const desde = Number(el.textContent.replace(/\D/g, '')) || 0;
    return contar(el, desde, valor, { duracion: 420, formato: mxn });
  }, [valor, periodo]);
  return <span ref={ref} />; // el texto lo escribe el efecto (React no lo toca)
}

// Lugares de precio fundador: un punto por lugar restante (si son pocos) que
// entra escalonado al ver la tarjeta, y el número cuenta hasta su valor.
function LugaresFundador({ n }) {
  const raiz = useRef(null);
  const num = useRef(null);
  const visto = useEnVista(raiz);
  const puntos = n <= 24;
  useEffect(() => {
    if (!visto || prefiereQuieto()) return;
    return contar(num.current, 0, n, { duracion: 700 });
  }, [visto, n]);
  return (
    <div ref={raiz} className="mb-4">
      <p className="text-sm font-semibold text-blue-700 dark:text-blue-300 flex items-center gap-2">
        <span aria-hidden="true" className="relative flex h-2 w-2 shrink-0">
          {visto && <span className="lp-ping absolute inset-0 rounded-full bg-blue-500" />}
          <span className="relative h-2 w-2 rounded-full bg-blue-600" />
        </span>
        <span>Quedan <span ref={num} className="tabular-nums">{n}</span> lugares de precio fundador</span>
      </p>
      {puntos && (
        <div aria-hidden="true" className="mt-2 flex flex-wrap gap-1">
          {Array.from({ length: n }, (_, i) => (
            <span key={i} style={{ '--i': i, opacity: visto ? undefined : 0 }} className={`h-1.5 w-3 rounded-full bg-blue-600/80 dark:bg-blue-400/80 ${visto ? 'lp-dot' : ''}`} />
          ))}
        </div>
      )}
    </div>
  );
}

function Precios({ planes }) {
  const [anual, setAnual] = useState(false);
  const { free, pro, trialDias } = planes;
  const precio = anual ? pro.anual : pro.mensual;
  const lista = anual ? pro.listaAnual : pro.listaMensual;
  const tachar = lista > precio;
  const sin = 'Sin límite';
  const NO = <span aria-label="No incluido">—</span>;
  const SI = <span aria-label="Incluido">✓</span>;
  const filas = [
    ['Transacciones al mes', free.transacciones, pro.transacciones || sin],
    ['Importar CFDI al mes', free.cfdi, sin],
    ['Cuentas', free.cuentas, sin],
    ['Tarjetas de crédito', free.tarjetas, sin],
    ['Presupuestos y metas', `${free.presupuestos} y ${free.metas}`, sin],
    ['Asistente con IA', '3 preguntas/mes', '100 preguntas/mes'],
    ['IA: informe, tickets, deducibles y más', NO, SI],
    ['Estimador de ISR e IVA', NO, SI],
    ['Espacios compartidos y proyectos', NO, SI],
    ['Gastos hormiga y recomendaciones', NO, SI],
    ['Patrimonio, deudas y suscripciones', NO, SI],
    ['Exportar', 'CSV', 'CSV y PDF'],
  ];

  const toggle = (activo) =>
    `relative z-10 min-h-[44px] px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-200 ${FOCUS} ${activo ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'}`;

  return (
    <section id="precios" className="py-20 px-4 bg-gray-50 dark:bg-slate-800/50 scroll-mt-16">
      <div className="max-w-5xl mx-auto">
        <Reveal><div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-black mb-3">Precios simples, en pesos</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            {trialDias} días de Pro al registrarte, sin tarjeta. Después eliges: plan gratis o Pro. Cancela cuando quieras.
          </p>
          <div role="group" aria-label="Periodo de cobro" className="inline-block bg-gray-200 dark:bg-slate-700 rounded-xl p-1">
            <div className="relative grid grid-cols-2">
              <span aria-hidden="true" style={{ transform: anual ? 'translateX(100%)' : 'translateX(0)' }} className="absolute inset-y-0 left-0 w-1/2 rounded-lg bg-white dark:bg-slate-600 shadow transition-transform duration-[260ms] ease-[cubic-bezier(0.77,0,0.175,1)] motion-reduce:transition-none" />
              <button type="button" aria-pressed={!anual} onClick={() => setAnual(false)} className={toggle(!anual)}>Mensual</button>
              <button type="button" aria-pressed={anual} onClick={() => setAnual(true)} className={toggle(anual)}>Anual</button>
            </div>
          </div>
        </div></Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start max-w-3xl mx-auto">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border-2 border-gray-200 dark:border-slate-700 p-7">
            <div className="inline-block text-xs font-bold px-2.5 py-1 rounded-lg mb-4 bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-gray-200">Gratis</div>
            <div className="text-4xl font-black mb-6">$0<span className="text-base font-normal text-gray-600 dark:text-gray-300">/mes</span></div>
            <Link to="/registro" className={`block text-center font-semibold py-3 rounded-xl mb-6 text-sm bg-gray-800 hover:bg-gray-900 dark:bg-slate-600 dark:hover:bg-slate-500 text-white lp-press ${FOCUS}`}>Empezar gratis</Link>
            <ul className="space-y-2.5 text-sm text-gray-700 dark:text-gray-200">
              {[
                `Hasta ${free.transacciones} transacciones al mes`,
                `Hasta ${free.cfdi} CFDI al mes`,
                `Hasta ${free.cuentas} cuentas y ${free.tarjetas} tarjetas`,
                `${free.presupuestos} presupuestos y ${free.metas} metas`,
                'Ingresos en MXN y USD',
                'Panel con disponible del día',
                'Instalable en tu celular (PWA)',
              ].map(f => <li key={f} className="flex gap-2"><span aria-hidden="true" className="text-green-600 dark:text-green-400 shrink-0">✓</span>{f}</li>)}
            </ul>
          </div>

          <div className="relative bg-white dark:bg-slate-800 rounded-2xl border-2 border-blue-500 ring-2 ring-blue-500 p-7 shadow-2xl shadow-blue-500/10">
            <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">Precio fundador</span>
            <div className="inline-block text-xs font-bold px-2.5 py-1 rounded-lg mb-4 bg-blue-600 text-white">Pro</div>
            <div className="mb-1 flex items-baseline gap-2 flex-wrap">
              <span className="text-4xl font-black tabular-nums"><PrecioAnimado valor={precio} periodo={anual} />{' '}<span key={String(anual)} className="lp-swap inline-block text-base font-normal text-gray-600 dark:text-gray-300">MXN/{anual ? 'año' : 'mes'}</span></span>
              {tachar && <s key={String(anual)} className="lp-swap text-lg text-gray-500 dark:text-gray-400" aria-label={`Precio de lista ${mxn(lista)}`}>{mxn(lista)}</s>}
            </div>
            <p key={String(anual)} className="lp-swap text-sm text-gray-600 dark:text-gray-300 mb-1 min-h-[1.25rem]">
              {anual ? `Equivale a ${mxn(Math.round(precio / 12))}/mes` : 'Cobro mensual, sin plazo forzoso'}
            </p>
            {pro.lugares !== null && pro.lugares > 0 && (
              <LugaresFundador n={pro.lugares} />
            )}
            {(pro.lugares === null || pro.lugares <= 0) && <div className="mb-4" />}
            <Link to="/registro" className={`block text-center font-semibold py-3 rounded-xl mb-2 text-sm bg-blue-600 hover:bg-blue-700 text-white lp-press ${FOCUS}`}>
              Empieza gratis — {trialDias} días de Pro
            </Link>
            <p className="text-xs text-center text-gray-600 dark:text-gray-300 mb-6">Sin tarjeta de crédito</p>
            <ul className="space-y-2.5 text-sm text-gray-700 dark:text-gray-200">
              {[
                'Todo lo del plan gratis, con límites mucho mayores',
                pro.transacciones ? `Hasta ${pro.transacciones} transacciones al mes` : 'Volumen de transacciones para uso profesional',
                'IA: asistente, informe mensual, tickets y más',
                'Estimador de ISR e IVA (RESICO y honorarios)',
                'Importación de CFDI sin tope mensual',
                'Hogar, contador de solo lectura y proyectos',
                'Patrimonio, deudas, suscripciones e ingresos recurrentes',
                'Gastos hormiga, recomendaciones y pronóstico',
                'Exportar en PDF y CSV',
              ].map(f => <li key={f} className="flex gap-2"><span aria-hidden="true" className="text-green-600 dark:text-green-400 shrink-0">✓</span>{f}</li>)}
            </ul>
          </div>
        </div>
        <Reveal><div className="max-w-3xl mx-auto mt-10 overflow-x-auto rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
          <table className="w-full text-sm text-left">
            <caption className="sr-only">Comparación entre el plan Gratis y el plan Pro</caption>
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-700/50 text-gray-800 dark:text-gray-100">
                <th scope="col" className="px-4 py-3 font-semibold">Qué incluye</th>
                <th scope="col" className="px-4 py-3 font-semibold">Gratis</th>
                <th scope="col" className="px-4 py-3 font-semibold">Pro</th>
              </tr>
            </thead>
            <tbody className="text-gray-700 dark:text-gray-200">
              {filas.map(([k, g, p]) => (
                <tr key={k} className="border-t border-gray-100 dark:border-slate-700">
                  <th scope="row" className="px-4 py-2.5 font-medium">{k}</th>
                  <td className="px-4 py-2.5">{g}</td>
                  <td className="px-4 py-2.5">{p}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div></Reveal>
        <p className="text-center text-xs text-gray-600 dark:text-gray-300 mt-6">
          Precios en pesos mexicanos. El precio fundador aplica a los primeros lugares disponibles.
        </p>
      </div>
    </section>
  );
}

export default function Landing() {
  usePageMeta(
    'MONIMEX: app para controlar tus gastos en México',
    'App de finanzas personales para México: controla tus gastos, detecta gastos hormiga y arma tu presupuesto por quincena, sin conectar tu banco.'
  );
  const planes = usePlanesPublicos();
  const { free, trialDias } = planes;
  const [filtro, setFiltro] = useState('todo');
  const [faqAbierta, setFaqAbierta] = useState(null);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 text-gray-900 dark:text-white">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_JSONLD) }} />
      <a href="#contenido" className="sr-only focus:not-sr-only focus:absolute focus:z-[60] focus:bg-white focus:text-blue-700 focus:px-4 focus:py-2 focus:m-2 focus:rounded">Saltar al contenido</a>

      <nav aria-label="Principal" className="sticky top-0 z-50 bg-white/90 dark:bg-slate-900/90 backdrop-blur border-b border-gray-100 dark:border-slate-800">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" aria-label="MONIMEX, inicio" className={`flex items-center min-h-[44px] rounded ${FOCUS}`}><Logo iconClassName="h-8 w-8" textClassName="text-xl" /></Link>
          <div className="flex items-center gap-1 sm:gap-3">
            <a href="#funciones" className={`hidden sm:inline text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-3 py-1.5 rounded ${FOCUS}`}>Funciones</a>
            <a href="#ia" className={`hidden sm:inline text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-3 py-1.5 rounded ${FOCUS}`}>IA</a>
            <a href="#precios" className={`hidden sm:inline text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-3 py-1.5 rounded ${FOCUS}`}>Precios</a>
            <Link to="/login" className={`inline-flex items-center min-h-[44px] whitespace-nowrap text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition px-2 sm:px-3 py-1.5 rounded ${FOCUS}`}><span className="sm:hidden">Entrar</span><span className="hidden sm:inline">Iniciar sesión</span></Link>
            <Link to="/registro" className={`inline-flex items-center min-h-[44px] whitespace-nowrap text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-1.5 rounded-lg lp-press ${FOCUS}`}>Empezar<span className="hidden sm:inline"> gratis</span></Link>
          </div>
        </div>
      </nav>

      <main id="contenido">
        {/* HERO */}
        <section className="relative overflow-hidden pt-16 sm:pt-20 pb-20 px-4">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 -z-10" />
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 text-xs font-semibold px-3 py-1 rounded-full mb-6 tracking-wide uppercase">
              Control de gastos y presupuesto para México
            </span>
            <h1 className="text-4xl sm:text-6xl font-black leading-tight mb-6">
              Descubre en qué se va tu dinero y{' '}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                deja de gastar de más.
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-700 dark:text-gray-300 max-w-2xl mx-auto mb-10 leading-relaxed">
              Registra tus ingresos y gastos, mira cuánto se te va en cada categoría y en gastos hormiga, y sabe cuánto puedes gastar hoy.
              Con presupuesto por quincena y avisos antes de pasarte.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/registro" className={BTN_PRIMARIO}>Empieza gratis — {trialDias} días de Pro</Link>
              <a href="#demo" className={BTN_SECUNDARIO}>Míralo en acción</a>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-4">Sin tarjeta de crédito · Después, plan gratuito o Pro desde el precio fundador</p>
          </div>

          <HeroDemo />
        </section>

        {/* FRANJA (cifras reales de los planes; cuentan al entrar en pantalla) */}
        <section aria-label="Datos clave" className="py-8 px-4 bg-blue-700 dark:bg-blue-800">
          <ul className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6 text-center text-white">
            <Dato valor={trialDias} sufijo=" días" label="de Pro sin tarjeta al registrarte" />
            <Dato valor={free.transacciones} label="transacciones al mes en el plan gratis" />
            <Dato valor={free.presupuestos} label="presupuestos con aviso al 80 % y 100 %" />
            <Dato valor={100} label="preguntas al mes al asistente con IA (Pro)" />
          </ul>
        </section>

        <MiniDemoGastos />

        {/* FUNCIONES POR GRUPO */}
        <section id="funciones" className="py-20 px-4 bg-gray-50 dark:bg-slate-800/50 scroll-mt-16">
          <div className="max-w-6xl mx-auto">
            <Reveal><div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-black mb-3">Todo para poner tu dinero en orden</h2>
              <p className="text-gray-600 dark:text-gray-300 text-lg max-w-2xl mx-auto mb-6">
                Toca una tarjeta para ver el detalle. Lo marcado con <ProBadge /> es del plan Pro.
              </p>
              <FiltroPlan valor={filtro} onChange={setFiltro} />
            </div></Reveal>
            <div className="space-y-14">
              {GRUPOS.map(g => <GrupoFunciones key={g.id} g={g} filtro={filtro} />)}
            </div>
          </div>
        </section>

        <SeccionIA />

        {/* CÓMO FUNCIONA */}
        <section className="py-20 px-4">
          <div className="max-w-5xl mx-auto">
            <Reveal><div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-black mb-3">Cómo funciona MONIMEX, sin conectar tu banco</h2>
              <p className="text-gray-600 dark:text-gray-300 text-lg">Tú controlas qué información entra.</p>
            </div></Reveal>
            <ol className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {PASOS.map((s, i) => (
                <li key={s.n}>
                  <Reveal delay={i * 120} className="text-center">
                    <div className="w-14 h-14 rounded-full bg-blue-600 text-white text-2xl font-black flex items-center justify-center mx-auto mb-4">{s.n}</div>
                    <h3 className="font-bold text-lg mb-2">{s.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">{s.desc}</p>
                  </Reveal>
                </li>
              ))}
            </ol>
            <p className="text-center text-sm text-gray-600 dark:text-gray-300 mt-10 max-w-2xl mx-auto">
              Lo que todavía no hacemos: conectarnos a tu banco (nunca te pediremos su contraseña) ni tener app nativa en tiendas. Es una PWA que instalas desde el navegador.
            </p>
          </div>
        </section>

        {/* FACTURAS Y DÓLARES (secundaria) */}
        <section id="facturas" className="py-20 px-4 bg-gray-50 dark:bg-slate-800/50 scroll-mt-16">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-3 gap-8 items-center mb-10">
              <Reveal className="lg:col-span-2">
                <h2 className="text-3xl sm:text-4xl font-black mb-3">{GRUPO_FREELANCE.titulo}</h2>
                <p className="text-gray-600 dark:text-gray-300 text-lg">{GRUPO_FREELANCE.sub}</p>
              </Reveal>
              <Reveal delay={100}><EjemploUSD /></Reveal>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {GRUPO_FREELANCE.items.map((f, i) => (
                <li key={f.title}>
                  <Reveal delay={(i % 3) * 90} className="h-full">
                    <FeatureCard f={f} tenue={tenueSegun(filtro, !!f.pro)} H="h3" />
                  </Reveal>
                </li>
              ))}
            </ul>
            <p className="text-center mt-8">
              <Link to="/calculadora-resico" className={`inline-flex items-center min-h-[44px] font-semibold text-blue-700 dark:text-blue-300 underline rounded ${FOCUS}`}>Calculadora RESICO gratis, sin registro</Link>
              <span aria-hidden="true" className="mx-2 text-gray-400">·</span>
              <Link to="/calculadora-gastos-hormiga" className={`inline-flex items-center min-h-[44px] font-semibold text-blue-700 dark:text-blue-300 underline rounded ${FOCUS}`}>Calculadora de gastos hormiga</Link>
              <span aria-hidden="true" className="mx-2 text-gray-400">·</span>
              <Link to="/calculadora-quincena" className={`inline-flex items-center min-h-[44px] font-semibold text-blue-700 dark:text-blue-300 underline rounded ${FOCUS}`}>Reparte tu quincena</Link>
            </p>
          </div>
        </section>

        {/* CONFIANZA */}
        <section className="py-16 px-4 bg-slate-900 text-white">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-black mb-3">Tus datos son tuyos</h2>
            <p className="text-slate-200 mb-6 leading-relaxed">
              Exporta todo en JSON o elimina tu cuenta cuando quieras. No vendemos tus datos, nunca pedimos la contraseña de tu banco y la IA no usa tus datos para entrenar modelos.
            </p>
            <Link to="/seguridad" className={`inline-flex items-center gap-2 bg-white text-slate-900 font-bold px-6 py-3 rounded-xl hover:bg-gray-100 lp-press ${FOCUS}`}>
              Ver cómo protegemos tus datos
            </Link>
          </div>
        </section>

        <Precios planes={planes} />

        {/* INVITA Y GANA: después de Precios (ya se entiende qué vale 1 mes de Pro) y antes del FAQ */}
        <LandingReferidos trialDias={trialDias} />

        {/* FAQ */}
        <section id="faq" className="py-20 px-4 scroll-mt-16">
          <div className="max-w-3xl mx-auto">
            <Reveal><div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-black mb-3">Preguntas frecuentes</h2>
            </div></Reveal>
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 px-6">
              {FAQS.map((faq, i) => <FaqItem key={faq.q} id={i} {...faq} open={faqAbierta === i} onToggle={() => setFaqAbierta(a => (a === i ? null : i))} />)}
            </div>
          </div>
        </section>

        {/* CTA FINAL */}
        <section className="py-16 px-4 bg-gradient-to-r from-blue-700 to-purple-800">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">Empieza hoy, sin tarjeta</h2>
            <p className="text-blue-50 text-lg mb-8">{trialDias} días de Pro para ver en qué se va tu dinero y cuánto puedes gastar hoy.</p>
            <Link to="/registro" className={`inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-blue-800 font-bold px-9 py-4 rounded-xl text-base lp-press shadow-xl ${FOCUS}`}>
              Empieza gratis — {trialDias} días de Pro
            </Link>
            <p className="text-blue-50 text-sm mt-4">
              Al registrarte aceptas nuestros{' '}
              <Link to="/terminos" className="underline">Términos de Servicio</Link>
              {' '}y el{' '}
              <Link to="/privacidad" className="underline">Aviso de Privacidad</Link>.
            </p>
          </div>
        </section>
      </main>

      <footer className="py-10 px-4 bg-gray-50 dark:bg-slate-900 border-t border-gray-100 dark:border-slate-800">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mb-6">
            <Logo iconClassName="h-7 w-7" textClassName="text-lg" />
            <nav aria-label="Pie de página" className="flex flex-wrap justify-center gap-x-6 gap-y-0 text-sm text-gray-700 dark:text-gray-300">
              <Link to="/planes" className={`inline-flex items-center justify-center min-w-[44px] min-h-[44px] hover:underline rounded ${FOCUS}`}>Planes</Link>
              <Link to="/calculadora-resico" className={`inline-flex items-center justify-center min-w-[44px] min-h-[44px] hover:underline rounded ${FOCUS}`}>Calculadora RESICO</Link>
              <Link to="/calculadora-gastos-hormiga" className={`inline-flex items-center justify-center min-w-[44px] min-h-[44px] hover:underline rounded ${FOCUS}`}>Gastos hormiga</Link>
              <Link to="/calculadora-quincena" className={`inline-flex items-center justify-center min-w-[44px] min-h-[44px] hover:underline rounded ${FOCUS}`}>Reparte tu quincena</Link>
              <Link to="/seguridad" className={`inline-flex items-center justify-center min-w-[44px] min-h-[44px] hover:underline rounded ${FOCUS}`}>Seguridad</Link>
              <Link to="/login" className={`inline-flex items-center justify-center min-w-[44px] min-h-[44px] hover:underline rounded ${FOCUS}`}>Iniciar sesión</Link>
              <Link to="/registro" className={`inline-flex items-center justify-center min-w-[44px] min-h-[44px] hover:underline rounded ${FOCUS}`}>Crear cuenta gratis</Link>
              <Link to="/terminos" className={`inline-flex items-center justify-center min-w-[44px] min-h-[44px] hover:underline rounded ${FOCUS}`}>Términos de Servicio</Link>
              <Link to="/privacidad" className={`inline-flex items-center justify-center min-w-[44px] min-h-[44px] hover:underline rounded ${FOCUS}`}>Aviso de Privacidad</Link>
            </nav>
          </div>
          <div className="border-t border-gray-200 dark:border-slate-800 pt-6 text-xs text-gray-600 dark:text-gray-400 text-center sm:text-left">
            © 2026 MONIMEX · Hecho en México 🇲🇽
          </div>
        </div>
      </footer>
    </div>
  );
}
